// src/services/messaging/messaging.service.interface.ts

import type { FileMetadata, HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices } from "@/types/poc/libp2p";
import type { MessagePayload } from "@/types/poc/message";

/**
 * Result types for different message handlers
 */
export interface FileUploadedResult {
  cid: string;
  metadata: FileMetadata;
  sender: string;
  shouldReplicate: boolean;
  messageTimestamp: number;
}

export interface FilesUpdatedResult {
  sender: string;
  files: Array<{
    cid: string;
    metadata: FileMetadata;
    shouldReplicate: boolean;
    messageTimestamp: number;
  }>;
}

export interface FileClonedResult {
  cid: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileUploadedAcknowledgedResult {
  messageId: string;
  sender: string;
  messageTimestamp: number;
}

export interface CheckForKeysNeedingRegeneratingResult {
  sixId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationStartedResult {
  cid: string;
  sixId: string;
  totalBytes: number;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationProgressResult {
  cid: string;
  sixId: string;
  stage: 'fetching' | 'registering';
  bytesTransferred: number;
  totalBytes: number;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

export interface FileReplicationFailedResult {
  cid: string;
  sixId: string;
  reason: string;
  targetPeerId: string;
  sender: string;
  messageTimestamp: number;
}

/**
 * Union type for all possible message handler results
 */
export type MessageHandlerResult =
  | { type: 'file-ready-for-download'; data: FileUploadedResult }
  | { type: 'files-updated'; data: FilesUpdatedResult }
  | { type: 'file-cloned'; data: FileClonedResult }
  | { type: 'file-ready-for-download-acknowledged'; data: FileUploadedAcknowledgedResult }
  | { type: 'file-key-ready-for-download-acknowledged'; data: FileUploadedAcknowledgedResult }
  | { type: 'check-for-keys-needing-regenerating'; data: CheckForKeysNeedingRegeneratingResult }
  | { type: 'file-replication-started'; data: FileReplicationStartedResult }
  | { type: 'file-replication-progress'; data: FileReplicationProgressResult }
  | { type: 'file-replication-failed'; data: FileReplicationFailedResult };

/**
 * Message statistics for observability
 */
export interface MessageStats {
  fromPeer: string;
  topic: string;
  timestamp: number;
  messageType: string;
  messageContent?: string; // Optional actual message content for display
}

/**
 * Abstract class defining the contract for the Messaging Service.
 * This service handles all pub/sub messaging operations in a pure, side-effect-free manner.
 */
export abstract class MessagingService {
  /**
   * Setup message subscriptions for a node.
   *
   * @param libp2pNode - The libp2p node to subscribe with
   * @param heliaNode - The Helia node (optional, for file operations)
   * @param onMessage - Callback function that receives processed messages and stats
   * @returns A cleanup function that unsubscribes from all topics
   */
  abstract setupSubscriptions(
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null,
    onMessage: (result: MessageHandlerResult, stats: MessageStats) => void,
    topics: string[]
  ): () => void;

  /**
   * Process a raw message and return structured data.
   * This is a pure function - no side effects.
   *
   * @param topic - The topic the message was received on
   * @param message - The raw message payload
   * @param libp2pNode - The libp2p node
   * @param heliaNode - The Helia node (optional)
   * @returns Processed message data or null if message should be ignored
   */
  abstract processMessage(
    topic: string,
    message: MessagePayload,
    libp2pNode: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): Promise<MessageHandlerResult | null>;

  /**
   * Publish a message to a specific topic.
   *
   * @param libp2pNode - The libp2p node to publish from
   * @param topic - The topic to publish to
   * @param message - The message to publish
   */
  abstract publishMessage(
    libp2pNode: LibP2pWithServices,
    topic: string,
    message: MessagePayload
  ): Promise<void>;

  /**
   * Publish the list of subscribed topics to the topic list channel.
   *
   * @param libp2pNode - The libp2p node to publish from
   * @param sixId - The six ID these topics belong to
   * @param topics - The list of topics to publish
   */
  abstract publishTopicList(
    libp2pNode: LibP2pWithServices,
    sixId: string,
    topics: string[]
  ): Promise<void>;

  /**
   * Get a list of all topics this service can handle.
   *
   * @returns Array of topic strings
   */
  abstract getSupportedTopics(): string[];
}
